import React, { useCallback, useEffect, useState } from 'react';
import { Form, Input, Table, Button, DatePicker, Popconfirm } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { RcFile } from 'antd/es/upload';

import { DefaultOptionType } from 'antd/es/select';
import SearchableSelect from '../../../components/SearchableSelect/index.tsx';
import { ExpenseEntriesProps, ExpenseFile } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { downloadExpenseAttachment } from 'src/api/api';

import { useFormValidation } from '../hooks/useFormValidation';
import { AmountInput } from './AmountInput';
import { FileUpload } from './FileUpload';

// Responsive breakpoint for mobile layout
const MOBILE_BREAKPOINT = 641;

/**
 * ExpenseEntries Component
 *
 * Renders a dynamic table for managing individual expense entries with file uploads.
 * Each row represents a single expense item with cost type, amount, date, and optional receipt.
 *
 * Key Features:
 * - Dynamic row addition/removal
 * - File upload for receipts (images and PDFs)
 * - Cost type searching and selection
 * - Amount validation and formatting
 * - Date picker with constraints
 * - Form validation integration
 *
 * @param props - ExpenseEntriesProps containing form data and handlers
 * @returns React component for expense entries management
 */
const ExpenseEntries: React.FC<ExpenseEntriesProps> = ({
  token,
  status,
  headerId,
  form,
  files,
  setFiles,
  setFormChanged,
  costTypes,
  costTypesLoading,
  costTypesPagination,
  onCostTypeSearch,
  maxMinDates,
  approving,
  multipleRows,
}): JSX.Element => {
  const { t } = useTranslation();
  const { createRequiredFieldRules, createRequiredOnlyRules } =
    useFormValidation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  /**
   * @description Formats a number as a string with commas and two decimal places.
   * @param value - The value to format.
   * @returns {string} The formatted number.
   */
  const formatNumber = (value: string): string => {
    // Convert the string to a number
    const numberValue = parseFloat(value.replace(/\s/g, '')); // remove any spaces if already present

    // Use Intl.NumberFormat for formatting
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    })
      .format(numberValue)
      .replace(/,/g, ',');
  };

  /**
   * @description Validates the amount field for AmountInput component.
   * @param rowIndex - The index of the row.
   * @returns {Promise} - A promise that resolves if the amount is valid, and rejects if it is not.
   */
  const onValidateAmount = async (rowIndex: number): Promise<void> => {
    const nokValue =
      form.getFieldValue(['expenses', rowIndex, 'nok'])?.replace(' ', '') ||
      '0';
    const oreValue = form.getFieldValue(['expenses', rowIndex, 'ore']) || '0';
    const value = parseFloat(`${nokValue}.${oreValue}`);

    // Check if both NOK and Øre are zero
    if (value === 0) {
      throw new Error(t('Amount cannot be zero for both NOK and Øre.'));
    }
  };

  /**
   * @description Downloads an attachment.
   * @param token - The authentication token.
   * @param headerId - The ID of the header.
   * @param rowId - The ID of the row.
   */
  const downloadAttachment = async (
    token: string,
    headerId: string,
    rowId: string
  ) => {
    try {
      const response = await downloadExpenseAttachment(token, headerId, rowId);
      const contentType = response.headers['content-type'];
      const contentDisposition = response.headers['content-disposition'];

      let fileName = 'downloaded-file'; // Default filename
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1];
        }
      }

      // Create a new Blob object using the response data and inferred MIME type
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url); // Clean up
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * @description Changes the file.
   * @param id - The ID of the file.
   * @param index - The index of the file.
   * @param file - The file to change to.
   * @returns {boolean} - Whether the file was changed.
   */
  const changeFile = (id: string, index: number, file?: RcFile): boolean => {
    if (file) {
      setFiles((prevFiles: ExpenseFile[]) =>
        prevFiles.map((expenseFile) =>
          expenseFile.id === id
            ? { ...expenseFile, file: file as File }
            : expenseFile
        )
      );
      return false; // Prevent the automatic upload
    } else {
      setFiles(() =>
        files.map((expenseFile) =>
          expenseFile.id === id
            ? { ...expenseFile, file: null, fileList: undefined }
            : expenseFile
        )
      );
      if (index) form.setFieldValue(['expenses', index, 'file_path'], '');
      return true;
    }
  };

  /**
   * @description Adds a new row to the expenses form.
   * A new UUID is generated for the row, and a corresponding entry is added to the files array.
   */
  const addRow = useCallback(() => {
    const expenses = form.getFieldValue('expenses') || [];
    const newId = uuidv4();

    form.setFieldsValue({
      expenses: [
        ...expenses,
        {
          id: newId,
          date: maxMinDates.maxDate,
          file_path: '',
          nok: '0',
          ore: '00',
        },
      ],
    });

    setFiles([
      ...files,
      {
        id: newId,
      },
    ]);
  }, [form, maxMinDates.maxDate, files, setFiles]);

  // Structural and UI initialization effect
  useEffect(() => {
    if (multipleRows) {
      // Add initial row if no expenses exist and status is empty
      if (!form.getFieldValue('expenses') && status === '') {
        addRow();
      }

      // Set up window resize listener for responsive behavior
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };

      window.addEventListener('resize', handleResize);

      // Cleanup event listener on unmount
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    } else {
      // Single row mode initialization
      const currentExpenses = form.getFieldValue('expenses') || [];

      // Add initial expense if none exist and status is empty
      if (!currentExpenses.length && status === '') {
        addRow();
      }
    }
  }, [form, status, multipleRows, files, setFiles, addRow]);

  /**
   * Removes a row from the expenses form based on the provided index and id.
   *
   * @param {number} index - The index of the row to be removed.
   * @param {string} id - The id of the file associated with the row to be removed.

   */
  const removeRow = useCallback(
    (index: number, id: string) => {
      const expenses: API.expenseRow[] = form.getFieldValue('expenses') || [];
      form.setFieldsValue({
        expenses: expenses.filter((_, idx: number) => idx !== index),
      });
      setFiles(() => files.filter((expenseFile) => expenseFile.id !== id));
      setFormChanged(true);
    },
    [form, files, setFiles, setFormChanged]
  );

  /**
   * @description Defines the columns for the expenses table.
   * @type {Array<Object>}
   * @property {string} title - The title of the column.
   * @property {string} dataIndex - The data index of the column.
   * @property {string} key - The key of the column.
   * @property {number} width - The width of the column.
   * @property {Function} render - The render function for the column.
   * @returns {Array<Object>} The columns for the expenses table.
   */
  const columns: Array<object> = [
    {
      title: t('Title'),
      dataIndex: 'title',
      key: 'title',
      width: '25%',
      render: (_: any, record: API.expenseRow, index: number) => (
        <Form.Item
          name={[index, 'title']}
          rules={createRequiredFieldRules('title')}
          label={windowWidth < MOBILE_BREAKPOINT ? t('Title') : null}
        >
          <Input autoComplete="off" />
        </Form.Item>
      ),
    },
    {
      title: t('Cost Type'),
      dataIndex: 'costType',
      key: 'costType',
      width: '15%',
      render: (_: any, record: API.expenseRow, index: number) => (
        <Form.Item
          name={[index, 'expense_account_id']}
          rules={createRequiredOnlyRules('Cost Type')}
          label={windowWidth < MOBILE_BREAKPOINT ? t('Cost Type') : null}
        >
          <SearchableSelect
            placeholder={t('Select cost type here')}
            options={Array.from(new Set(costTypes.map((item) => item.value)))
              .map((value) => costTypes.find((item) => item.value === value))
              .filter((item): item is DefaultOptionType => item !== undefined)}
            allowClear={false}
            style={{ maxWidth: '140px' }}
            loading={costTypesLoading}
            onSearch={onCostTypeSearch}
            paginationResponse={costTypesPagination}
          />
        </Form.Item>
      ),
    },
    {
      title: t('Date'),
      dataIndex: 'rowDate',
      key: 'rowDate',
      width: '15%',
      render: (_: any, record: API.expenseRow, index: number) => (
        <Form.Item
          name={[index, 'date']}
          rules={createRequiredOnlyRules('Date')}
          label={windowWidth < MOBILE_BREAKPOINT ? t('Date') : null}
        >
          <DatePicker
            format={['DD-MM-YYYY']}
            minDate={maxMinDates.minDate}
            maxDate={maxMinDates.maxDate}
          />
        </Form.Item>
      ),
    },
    {
      title: `${t('Amount')} (NOK)`,
      dataIndex: 'amount',
      key: 'amount',
      width: '20%',
      render: (_: any, record: API.expenseRow, index: number) => (
        <AmountInput
          index={index}
          form={form}
          onValidateAmount={onValidateAmount}
        />
      ),
    },
    {
      title: t('Attachments'),
      dataIndex: 'file_path',
      key: 'file_path',
      width: '10%',
      render: (_: any, record: API.expenseRow, index: number) => (
        <FileUpload
          index={index}
          recordId={record.id}
          headerId={headerId}
          token={token}
          files={files}
          approving={approving}
          filePath={record.file_path}
          onFileChange={changeFile}
          onDownload={downloadAttachment}
        />
      ),
    },
    status !== 'sent'
      ? {
          title: t('Action'),
          dataIndex: 'action',
          key: 'action',
          width: '5%',
          render: (_: any, record: API.expenseRow, index: number) => (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Popconfirm
                title={t('Sure to delete?')}
                onConfirm={() => removeRow(index, record.id)}
              >
                <CloseOutlined />
              </Popconfirm>
            </div>
          ),
        }
      : {},
  ];

  /**
   * if multipleRows is true, render a table with the columns defined above.
   * else, render a single row with inputs for title, cost type, date, amount, and attachments.
   */
  if (multipleRows) {
    return (
      <>
        <Button onClick={addRow} type="primary" style={{ marginBottom: 16 }}>
          {t('Add Row')}
        </Button>
        <Form.List name="expenses">
          {(fields) => (
            <>
              <Table
                dataSource={fields.map((field) => ({
                  ...field,
                  ...form.getFieldValue(['expenses', field.key]),
                }))}
                columns={columns}
                rowKey={(record) => record.key ?? ''}
                pagination={false}
                size="small"
                className="aex-table"
                summary={() => {
                  return (
                    <>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                        <Table.Summary.Cell index={1}></Table.Summary.Cell>
                        <Table.Summary.Cell index={2}></Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>
                          <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                              const expenses = getFieldValue('expenses') || [];
                              const totalAmount = expenses
                                .reduce((sum: number, item: API.expenseRow) => {
                                  const amount = Number(item.amount) || 0;
                                  return sum + amount;
                                }, 0)
                                .toFixed(2);

                              return <pre>NOK {formatNumber(totalAmount)}</pre>;
                            }}
                          </Form.Item>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </>
                  );
                }}
              />
            </>
          )}
        </Form.List>
      </>
    );
  } else {
    return (
      <div className="aex-single-expense">
        <Form.Item
          name={['expenses', 0, 'expense_account_id']}
          rules={[
            {
              required: true,
              message: t('Please select the') + ' ' + t('Cost Type') + '!',
            },
          ]}
          label={t('Cost Type')}
        >
          <SearchableSelect
            placeholder={t('Select cost type here')}
            options={Array.from(new Set(costTypes.map((item) => item.value)))
              .map((value) => costTypes.find((item) => item.value === value))
              .filter((item): item is DefaultOptionType => item !== undefined)}
            allowClear={false}
            loading={costTypesLoading}
            onSearch={onCostTypeSearch}
            paginationResponse={costTypesPagination}
            disabled={status === 'sent' || status === 'failed'}
          />
        </Form.Item>
        <AmountInput
          index={0}
          form={form}
          onValidateAmount={onValidateAmount}
          disabled={status === 'sent' || status === 'failed'}
        />
        <FileUpload
          index={0}
          recordId={files[0]?.id || ''}
          headerId={headerId}
          token={token}
          files={files || null}
          approving={approving}
          onFileChange={changeFile}
          onDownload={downloadAttachment}
          disabled={status === 'sent' || status === 'failed'}
        />
      </div>
    );
  }
};

export default ExpenseEntries;
